import { ComponentType, JSX } from 'react';

const checkChunk = (text: string, startsWith: string, endsWith: string) => {
  return text.startsWith(startsWith) && text.endsWith(endsWith);
};

const checkIsLink = (text: string) => {
  return checkChunk(text, '[', ']');
};

const checkIsLinkTitle = (text: string) => {
  return checkChunk(text, '(', ')');
};

const checkIsBold = (text: string) => {
  return checkChunk(text, '*', '*');
};

const checkIsGray = (text: string) => {
  return checkChunk(text, '%', '%');
};

type ParsedChunk = {
  className?: string;
  href?: string;
  isBold?: boolean;
  isGray?: boolean;
  text: string;
};

const parseText = (text?: string): ParsedChunk[] => {
  if (!text) {
    return [];
  }

  const linksAndBoldAndGrayText = /(\(\b[^)]*?\)|\[[^[\]]*?]|\*[^*]*?\*|%[^*]*?%)/g;
  const splitText = text.split(linksAndBoldAndGrayText).filter(Boolean);

  if (!splitText.length) {
    return [];
  }

  const parsedChunks: ParsedChunk[] = [];

  splitText.forEach((chunk, index) => {
    const prevItem = splitText[index - 1] || '';
    const nextItem = splitText[index + 1] || '';

    const isLink = checkIsLink(chunk);
    const isLinkTitle = checkIsLinkTitle(chunk) && checkIsLink(nextItem);

    const parsedText = chunk.slice(1, -1);

    if (isLinkTitle) {
      return;
    }

    if (isLink) {
      const hasTitle = checkIsLinkTitle(prevItem);
      const titleWithoutBrackets = prevItem.replace(/[()]/g, '');

      parsedChunks.push({
        href: parsedText,
        text: hasTitle ? titleWithoutBrackets : parsedText,
      });
      return;
    }

    const isBold = checkIsBold(chunk);
    const isGray = checkIsGray(chunk);

    parsedChunks.push({
      className: isBold ? 'parsedBoldText' : 'parsedDefaultText',
      isBold,
      isGray,
      text: isBold || isGray ? parsedText : chunk,
    });
  });

  return parsedChunks;
};

export const createTextParser = (
  Link: ComponentType<any>,
  DescriptionText: ComponentType<any>,
): ((text?: string) => JSX.Element | string) => {
  return function (text?: string): JSX.Element | string {
    if (!text) {
      return '';
    }

    const parsedChunks = parseText(text);

    return (
      <>
        {parsedChunks.map((chunk) => {
          if (chunk.href) {
            return (
              <Link key={chunk.href} href={chunk.href} target="_blank" to={chunk.href}>
                {chunk.text}
              </Link>
            );
          }

          return (
            <DescriptionText key={chunk.text} $isBold={chunk.isBold} $isGray={chunk.isGray} className={chunk.className}>
              {chunk.text}
            </DescriptionText>
          );
        })}
      </>
    );
  };
};
